import { useMemo } from "react";
import {
  DATE_OPTIONS_DEFAULT,
  DATE_TIME_OPTIONS_DEFAULT,
  DEFAULT_CURRENCY,
  formatCountry,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatRelative,
  type DateInput,
} from "@tc/i18n";
import { useAuthz } from "@/lib/auth/authz";
import { useLanguage } from "./language";

/**
 * Locale-aware formatters bound to the active UI language (TW-3434) and the user's chosen timezone
 * (TW-3466). Thin wrappers over the shared `@tc/i18n` Intl helpers so components never hard-code a
 * locale:
 *
 *   const fmt = useFormatters();
 *   fmt.currency(1250)      // "1250,00 €" (es) · "€1,250.00" (en)
 *   fmt.date(trip.departAt) // "14 jun 2026" (es) · "14 Jun 2026" (en)
 *
 * Dates/times render in `me.timezone` when set (formatters are only used inside RoleLayout, where
 * the auth context is always present). We add the zone to whatever option set is in play — the
 * caller's, or the shared defaults — so existing partial-option callers keep their exact shape.
 */
export function useFormatters() {
  const { lang } = useLanguage();
  const { me } = useAuthz();
  const timeZone = me?.timezone ?? undefined;

  return useMemo(() => {
    const withZone = (base: Intl.DateTimeFormatOptions, options?: Intl.DateTimeFormatOptions) =>
      timeZone ? { ...(options ?? base), timeZone } : options;
    return {
      date: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
        formatDate(lang, value, withZone(DATE_OPTIONS_DEFAULT, options)),
      dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
        formatDateTime(lang, value, withZone(DATE_TIME_OPTIONS_DEFAULT, options)),
      number: (value: number, options?: Intl.NumberFormatOptions) => formatNumber(lang, value, options),
      currency: (value: number, currency: string = DEFAULT_CURRENCY, options?: Intl.NumberFormatOptions) =>
        formatCurrency(lang, value, currency, options),
      relative: (value: DateInput, base?: DateInput) => formatRelative(lang, value, base),
      country: (code: string) => formatCountry(lang, code),
    };
  }, [lang, timeZone]);
}
